import React from 'react';
import { BookOpen, Download, Crown, Sparkles, Check } from 'lucide-react';
import { useAppData } from '../../hooks/useAppData';
import { Capacitor } from '@capacitor/core';

interface SubscriptionModalProps {
  onClose: () => void;
  onSignup?: () => void;
  onLogin?: () => void;
  onSubscribe?: () => void;
  variant?: 'guest' | 'free-user';
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  onClose,
  onSignup,
  onLogin,
  onSubscribe,
  variant = 'free-user'
}) => {
  const { modalTexts } = useAppData();
  const isGuest = variant === 'guest';
  const isNative = Capacitor.isNativePlatform();

  // Texte offline personnalisé (plus court et engageant)
  const offlineText = "En avion, en voiture, profitez de vos histoires partout, même hors ligne.";

  return (
    <div className={`fixed inset-0 z-50 ${isNative ? 'bg-white' : 'bg-black bg-opacity-50 flex items-center justify-center p-4'}`}>
      <div className={`bg-white relative ${
        isNative
          ? 'w-full h-full overflow-y-auto safe-area-bottom'
          : 'rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto overflow-hidden'
      }`}>

        {/* Header avec gradient */}
        <div className={`bg-gradient-to-r from-purple-600 to-blue-600 text-white ${
          isNative ? 'p-6 pt-16 safe-area-top' : 'p-6'
        }`}>
          <div className="text-center">
            <div className={`mb-2 ${isNative ? 'text-4xl' : 'text-5xl'}`}>👑</div>
            <h2 className={`font-bold ${isNative ? 'text-xl' : 'text-2xl'}`}>
              {isGuest ? modalTexts.subscription.guest.title : modalTexts.subscription.premium.title}
            </h2>
            <p className="text-white text-opacity-90 text-sm mt-1">
              {isGuest ? modalTexts.subscription.guest.subtitle : modalTexts.subscription.premium.subtitle}
            </p>
          </div>
        </div>

        <div className={isNative ? 'p-4' : 'p-6'}>
          {/* Features */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                <BookOpen className="text-white" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm">{modalTexts.subscription.features.allBooks.title}</h3>
                <p className="text-xs text-gray-600 truncate">{modalTexts.subscription.features.allBooks.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Download className="text-white" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm">Accès hors ligne</h3>
                <p className="text-xs text-gray-600">{offlineText}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-yellow-600 flex items-center justify-center flex-shrink-0">
                <Crown className="text-white" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm">{modalTexts.subscription.features.unlimitedCollection.title}</h3>
                <p className="text-xs text-gray-600 truncate">{modalTexts.subscription.features.unlimitedCollection.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-pink-600 flex items-center justify-center flex-shrink-0">
                <Sparkles className="text-white" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-sm">{modalTexts.subscription.features.rareObjects.title}</h3>
                <p className="text-xs text-gray-600 truncate">{modalTexts.subscription.features.rareObjects.description}</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-2">
            {isGuest ? (
              <>
                <button
                  onClick={() => {
                    onClose();
                    if (onSignup) onSignup();
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all shadow-md"
                >
                  {modalTexts.subscription.buttons.createAccount}
                </button>
                <button
                  onClick={() => {
                    onClose();
                    if (onLogin) onLogin();
                  }}
                  className="w-full bg-white text-gray-700 py-2.5 rounded-lg font-medium border border-gray-300 hover:border-gray-400 transition-colors"
                >
                  {modalTexts.subscription.buttons.login}
                </button>
                <button
                  onClick={onClose}
                  className="w-full text-gray-400 py-2 rounded-lg text-sm hover:text-gray-600 transition-colors"
                >
                  {modalTexts.subscription.buttons.later}
                </button>
              </>
            ) : (
              <>
                {/* Prix compact */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg p-4 mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs opacity-90">{modalTexts.subscription.pricing.subscription}</div>
                      <div className="text-2xl font-bold">4.99€<span className="text-sm font-normal">{modalTexts.subscription.pricing.monthly}</span></div>
                    </div>
                    <div className="text-right text-xs opacity-90">
                      <div className="flex items-center gap-1"><Check size={12} /> {modalTexts.subscription.pricing.cancelAnytime}</div>
                      <div className="flex items-center gap-1"><Check size={12} /> {modalTexts.subscription.pricing.noCommitment}</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    onClose();
                    if (onSubscribe) onSubscribe();
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all shadow-md"
                >
                  {modalTexts.subscription.buttons.subscribe}
                </button>
                <button
                  onClick={onClose}
                  className="w-full text-gray-500 py-2 rounded-lg text-sm hover:text-gray-700 transition-colors"
                >
                  {modalTexts.subscription.buttons.later}
                </button>
              </>
            )}
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            {isGuest
              ? modalTexts.subscription.footer.guest
              : modalTexts.subscription.footer.premium
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
